import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  GoogleMap,
  DrawingManager,
  Polygon,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { HeatmapLayer as DeckHeatmapLayer } from '@deck.gl/aggregation-layers';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import {
  Spin,
  Row,
  Col,
  Button,
  notification,
  InputNumber,
  Slider,
  Collapse,
  Checkbox,
} from 'antd';
import { orderPolygonParameter } from '../../../../store/requests/order';

const containerStyle = {
  width: '100%',
  height: '800px',
};

const SHAPE_TYPE = {
  POLYGON: 'polygon',
  CIRCLE: 'circle',
  RECTANGLE: 'rectangle',
};

const OrderAnalysisMap = ({
  onLoad,
  onPolygonComplete,
  polygonPath,
  clearCoordinates,
  orderDetails,
  mapLoading,
  storeOrderSummaryList,
  allStoresMap,
  showAllStores,
  drawing,
  onIncludeDeliveryFromStoreChange,
}) => {
  const { t } = useTranslation();

  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoneVisibility, setZoneVisibility] = useState({});
  const [selectedZone, setSelectedZone] = useState(null);
  const [clusterer, setClusterer] = useState(null);
  const drawingManagerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapCenter, setMapCenter] = useState({
    lat: 41.065233,
    lng: 28.914577,
  });
  const polygonRef = useRef(null);
  const [isPolygonInitiallySet, setIsPolygonInitiallySet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLocationOrders, setSelectedLocationOrders] = useState([]);
  const [currentOrderIndex, setCurrentOrderIndex] = useState(0);
  const [maxAreaKm, setMaxAreaKm] = useState(80);
  const [heatmapRadius, setHeatmapRadius] = useState(40);
  const [heatmapOpacity, setHeatmapOpacity] = useState(0.8);
  const [heatmapMaxIntensity, setHeatmapMaxIntensity] = useState(
    Math.max(1, orderDetails ? Math.floor(orderDetails.length / 10) : 100),
  );
  const [showClusteredMarkers, setShowClusteredMarkers] = useState(true);
  const [includeDeliveryFromStore, setIncludeDeliveryFromStore] =
    useState(true);

  useEffect(() => {
    const fetchMaxArea = async () => {
      const response = await orderPolygonParameter('order_max_area_km');
      const maxKm = Number(response?.content?.[0]?.value);
      setMaxAreaKm(maxKm || 80);
    };
    fetchMaxArea();
  }, []);

  const options = {
    drawingControl: true,
    drawingControlOptions: {
      position: window.google.maps.ControlPosition.TOP_CENTER,
      drawingModes: [
        window.google.maps.drawing.OverlayType.POLYGON,
        window.google.maps.drawing.OverlayType.CIRCLE,
        window.google.maps.drawing.OverlayType.RECTANGLE,
      ],
    },
    polygonOptions: {
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      editable: true,
      draggable: true,
    },
    circleOptions: {
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
    },
    rectangleOptions: {
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
    },
  };

  const markerIcon = {
    url: '/static/img/ceptesokpin.svg',
    scaledSize: new window.google.maps.Size(30, 45),
  };

  const zonePolygonOptions = {
    fillColor: '#7e98c2',
    fillOpacity: 0.35,
    strokeColor: '#ffe000',
    strokeOpacity: 0.8,
    strokeWeight: 2,
  };

  const overlayRef = useRef(null);

  const generateGeoJSONCircle = (center, radius, numSides) => {
    let points = [];
    const degreeStep = 360 / numSides;
    for (let i = 0; i < numSides; i++) {
      let gpos = window.google.maps.geometry.spherical.computeOffset(
        center,
        radius,
        degreeStep * i,
      );
      points.push({ lat: gpos.lat(), lng: gpos.lng() });
    }
    points.push(points[0]);
    return points;
  };

  const onShapesComplete = (shape, shapeType) => {
    let coordinates = [];
    if (shapeType === SHAPE_TYPE.CIRCLE) {
      const radius = shape.getRadius();
      const center = shape.getCenter();
      coordinates = generateGeoJSONCircle(center, radius, 20);
      const newPolygon = new window.google.maps.Polygon({
        paths: coordinates,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        editable: true,
        draggable: false,
      });
      setPolygonPaths(newPolygon);
      shape.setMap(null);
    } else if (shapeType === SHAPE_TYPE.RECTANGLE) {
      const bounds = shape.getBounds();
      coordinates = [
        { lat: bounds.getNorthEast().lat(), lng: bounds.getNorthEast().lng() },
        { lat: bounds.getNorthEast().lat(), lng: bounds.getSouthWest().lng() },
        { lat: bounds.getSouthWest().lat(), lng: bounds.getSouthWest().lng() },
        { lat: bounds.getSouthWest().lat(), lng: bounds.getNorthEast().lng() },
      ];
      const newPolygon = new window.google.maps.Polygon({
        paths: coordinates,
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.35,
        editable: true,
        draggable: false,
      });
      setPolygonPaths(newPolygon);
      shape.setMap(null);
    } else if (shapeType === SHAPE_TYPE.POLYGON) {
      setPolygonPaths(shape);
    }
  };

  useEffect(() => {
    setLoading(false);
    setSelectedZone(null);
    setSelectedMarker(null);
    setZoneVisibility(false);
    setSelectedLocationOrders([]);
    setCurrentOrderIndex(0);
    setHeatmapMaxIntensity(
      Math.max(1, orderDetails ? Math.floor(orderDetails.length / 10) : 100),
    );
  }, [orderDetails]);

  const setPolygonPaths = (shape) => {
    if (shape.getPath) {
      const area = window.google.maps.geometry.spherical.computeArea(
        shape.getPath(),
      );
      const areaInKm2 = area / 1000000;

      if (areaInKm2 > maxAreaKm) {
        notification.error({
          message: t('error'),
          description: `${t('polygonTooLarge')} (${areaInKm2.toFixed(
            2,
          )} km² > ${maxAreaKm} km²)`,
        });
        shape.setMap(null);
        return;
      }

      onPolygonComplete({ getPath: () => shape.getPath() });
      shape.setMap(null);
    }
  };

  const onPolygonMouseUp = () => {
    if (polygonRef.current && polygonRef.current.getPath) {
      const area = window.google.maps.geometry.spherical.computeArea(
        polygonRef.current.getPath(),
      );
      const areaInKm2 = area / 1000000;

      if (areaInKm2 > maxAreaKm) {
        notification.error({
          message: t('error'),
          description: `${t('polygonTooLarge')} (${areaInKm2.toFixed(
            2,
          )} km² > ${maxAreaKm} km²)`,
        });

        const currentPath = polygonPath.map((coord) => ({
          lat: coord.latitude,
          lng: coord.longitude,
        }));
        polygonRef.current.setPath(currentPath);
      } else {
        onPolygonComplete({ getPath: () => polygonRef.current.getPath() });
      }
    }
  };

  const onPolygonLoad = (polygon) => {
    polygonRef.current = polygon;
  };

  const uniqueOrders = orderDetails
    ? orderDetails.reduce((acc, current) => {
        const key = `${current.store.latitude}-${current.store.longitude}`;
        if (
          !acc.some(
            (item) => `${item.store.latitude}-${item.store.longitude}` === key,
          )
        ) {
          acc.push(current);
        }
        return acc;
      }, [])
    : [];

  const normalizedStores =
    showAllStores && allStoresMap?.length > 0
      ? allStoresMap.map((store) => ({
          code: store.storeCode,
          name: store.storeName,
          type: store.storeType,
          ecomStatus: store.ecomStatus,
          active: store.active,
          latitude: store.latitude,
          longitude: store.longitude,
          zones: store.zones,
        }))
      : [];

  const allStores = [
    ...uniqueOrders.map((order) => order.store),
    ...normalizedStores,
  ].reduce((acc, store) => {
    if (!acc.find((s) => s.code === store.code)) {
      acc.push(store);
    }
    return acc;
  }, []);

  const heatmapData = useMemo(
    () =>
      orderDetails
        ? orderDetails.filter((order) => order.latitude && order.longitude)
        : [],
    [orderDetails],
  );

  useEffect(() => {
    if (!mapRef.current) return;

    if (heatmapData.length > 0) {
      const heatmapLayer = new DeckHeatmapLayer({
        id: 'order-heatmap',
        data: heatmapData,
        getPosition: (d) => [d.longitude, d.latitude],
        getWeight: 1,
        radiusPixels: heatmapRadius,
        opacity: heatmapOpacity,
        intensity: Math.max(
          1,
          Math.round(heatmapData.length / heatmapMaxIntensity),
        ),
        threshold: 0.05,
        aggregation: 'SUM',
      });

      if (!overlayRef.current) {
        overlayRef.current = new GoogleMapsOverlay({
          layers: [heatmapLayer],
        });
        overlayRef.current.setMap(mapRef.current);
      } else {
        overlayRef.current.setProps({ layers: [heatmapLayer] });
      }
    } else {
      if (overlayRef.current) {
        overlayRef.current.setProps({ layers: [] });
      }
    }
  }, [heatmapData, heatmapRadius, heatmapOpacity, heatmapMaxIntensity]);

  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.finalize();
        overlayRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (clusterer && orderDetails) {
      clusterer.clearMarkers();
      if (showClusteredMarkers) {
        const roundToFiveDecimals = (num) =>
          Math.round(num * 1000000) / 1000000;

        const newMarkers = orderDetails.map((order) => {
          const marker = new window.google.maps.Marker({
            position: { lat: order.latitude, lng: order.longitude },
          });
          marker.addListener('click', () => {
            const ordersAtLocation = orderDetails.filter(
              (o) =>
                roundToFiveDecimals(o.latitude) ===
                  roundToFiveDecimals(order.latitude) &&
                roundToFiveDecimals(o.longitude) ===
                  roundToFiveDecimals(order.longitude),
            );
            setSelectedLocationOrders(ordersAtLocation);
            setCurrentOrderIndex(0);
            setSelectedMarker(null);
            setSelectedZone(null);
          });
          return marker;
        });

        clusterer.addMarkers(newMarkers);
      }
    }
    return () => {
      if (clusterer) {
        clusterer.clearMarkers();
      }
    };
  }, [orderDetails, clusterer, showClusteredMarkers]);

  const showDrawingTools = drawing && polygonPath.length === 0;

  const getPolygonCenter = (coords) => {
    if (!coords || coords.length === 0) return { lat: 0, lng: 0 };
    const bounds = new window.google.maps.LatLngBounds();
    coords.forEach((coord) => {
      bounds.extend({ lat: coord.latitude, lng: coord.longitude });
    });
    return bounds.getCenter();
  };

  const preserveMapState = () => {
    if (mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      const currentCenter = mapRef.current.getCenter();
      return { zoom: currentZoom, center: currentCenter };
    }
    return null;
  };

  const restoreMapState = (state) => {
    if (mapRef.current && state) {
      mapRef.current.setZoom(state.zoom);
      mapRef.current.setCenter(state.center);
    }
  };

  const toggleZoneVisibility = (storeCode, isChecked) => {
    const mapState = preserveMapState();
    if (isChecked) {
      setZoneVisibility({ [storeCode]: true });
    } else {
      setZoneVisibility((prev) => {
        const newVisibility = { ...prev };
        delete newVisibility[storeCode];
        return newVisibility;
      });
      const store = allStores.find((s) => s.code === storeCode);
      if (store && store.zones?.includes(selectedZone)) {
        setSelectedZone(null);
      }
    }
    setTimeout(() => restoreMapState(mapState), 0);
  };

  const previousPolygonPathRef = useRef([]);

  useEffect(() => {
    if (
      polygonPath.length === 0 ||
      !isEqual(polygonPath, previousPolygonPathRef.current)
    ) {
      setIsPolygonInitiallySet(false);
    }
    previousPolygonPathRef.current = polygonPath;
  }, [polygonPath]);

  useEffect(() => {
    if (polygonPath.length > 0 && mapRef.current && !isPolygonInitiallySet) {
      const map = mapRef.current;
      const currentBounds = map.getBounds();
      const currentZoom = map.getZoom();
      const polygonCenter = getPolygonCenter(polygonPath);
      const polygonBounds = new window.google.maps.LatLngBounds();

      polygonPath.forEach((coord) => {
        polygonBounds.extend({ lat: coord.latitude, lng: coord.longitude });
      });

      if (!(currentBounds.contains(polygonCenter) && currentZoom >= 12)) {
        map.fitBounds(polygonBounds);
        setIsPolygonInitiallySet(true);
      }
    }
  }, [polygonPath, isPolygonInitiallySet]);

  const calculatePolygonArea = () => {
    if (polygonPath.length < 3) return 0;

    const polygon = new window.google.maps.Polygon({
      paths: polygonPath.map((coord) => ({
        lat: coord.latitude,
        lng: coord.longitude,
      })),
    });

    const area = window.google.maps.geometry.spherical.computeArea(
      polygon.getPath(),
    );
    return (area / 1000000).toFixed(2);
  };

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      zoom={10}
      center={mapCenter}
      onLoad={(map) => {
        mapRef.current = map;
        const newClusterer = new MarkerClusterer({ map });
        setClusterer(newClusterer);

        if (onLoad) onLoad(map);
      }}
      options={{
        gestureHandling: 'greedy',
        clickableIcons: false,
        zoomControl: true,
        disableDoubleClickZoom: true,
        disableDefaultUI: false,
        streetViewControl: false,
      }}
      onClick={() => {
        const mapState = preserveMapState();
        setSelectedMarker(null);
        setSelectedZone(null);
        restoreMapState(mapState);
      }}
    >
      {showDrawingTools && (
        <DrawingManager
          ref={drawingManagerRef}
          onPolygonComplete={(polygon) =>
            onShapesComplete(polygon, SHAPE_TYPE.POLYGON)
          }
          onCircleComplete={(circle) =>
            onShapesComplete(circle, SHAPE_TYPE.CIRCLE)
          }
          onRectangleComplete={(rectangle) =>
            onShapesComplete(rectangle, SHAPE_TYPE.RECTANGLE)
          }
          drawingMode={'polygon'}
          options={options}
        />
      )}
      {polygonPath.length > 0 && (
        <Polygon
          paths={polygonPath.map((coord) => ({
            lat: coord.latitude,
            lng: coord.longitude,
          }))}
          options={{
            fillColor: '#FF0000',
            fillOpacity: 0.1,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            editable: true,
            draggable: false,
            clickable: false,
          }}
          onLoad={onPolygonLoad}
          onMouseUp={onPolygonMouseUp}
        />
      )}
      {allStores.map((store, index) => {
        if (store && store.latitude && store.longitude) {
          return (
            <Marker
              key={index}
              position={{
                lat: store.latitude,
                lng: store.longitude,
              }}
              icon={markerIcon}
              onClick={(e) => {
                e.domEvent.stopPropagation();
                const mapState = preserveMapState();
                setSelectedMarker(store);
                setSelectedZone(null);
                restoreMapState(mapState);
              }}
            />
          );
        }
        return null;
      })}
      {selectedMarker && (
        <InfoWindow
          position={{
            lat: selectedMarker.latitude,
            lng: selectedMarker.longitude,
          }}
          onCloseClick={() => {
            const mapState = preserveMapState();
            setSelectedMarker(null);
            setSelectedZone(null);
            restoreMapState(mapState);
          }}
        >
          <div>
            <p>
              <b>{t('storeName')}:</b> {selectedMarker.name}
            </p>
            <p>
              <b>{t('storeCode')}:</b> {selectedMarker.code || '--'}
            </p>
            <p>
              <b>{t('storeType')}:</b> {selectedMarker.type || '--'}
            </p>
            <p>
              <b>{t('storeEcomStatus')}:</b> {selectedMarker.ecomStatus || '--'}
            </p>
            <p>
              <b>{t('storeStatus')}:</b>{' '}
              {selectedMarker.active === true
                ? t('OPEN')
                : selectedMarker.active === false
                  ? t('CLOSED')
                  : '--'}
            </p>
            <p>
              <b>{t('orderCount')}:</b>{' '}
              {storeOrderSummaryList?.find(
                (summary) => summary.storeCode === selectedMarker.code,
              )?.orderCount ?? '--'}
            </p>
            <p>
              <b>{t('courierCount')}:</b>{' '}
              {storeOrderSummaryList?.find(
                (summary) => summary.storeCode === selectedMarker.code,
              )?.courierCount ?? '--'}
            </p>
            <p>
              <b>{t('zones')}:</b>
            </p>
            {selectedMarker.zones && selectedMarker.zones.length > 0 ? (
              selectedMarker.zones.map((zone, index) => (
                <p key={index}>{zone.name || '--'}</p>
              ))
            ) : (
              <p>
                <b>{t('zoneName')}:</b> --
              </p>
            )}
            <label>
              <input
                type="checkbox"
                checked={!!zoneVisibility[selectedMarker.code]}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleZoneVisibility(selectedMarker.code, e.target.checked);
                }}
              />
              <span className="pl-5">{t('showZones')}</span>
            </label>
          </div>
        </InfoWindow>
      )}
      {allStores.map((store, index) =>
        store &&
        zoneVisibility[store.code] &&
        store.zones &&
        Array.isArray(store.zones)
          ? store.zones.map((zone, zoneIndex) =>
              zone &&
              zone.zonePolygonJson &&
              Array.isArray(zone.zonePolygonJson) ? (
                <Polygon
                  key={`${index}-${zoneIndex}`}
                  paths={zone.zonePolygonJson.map((coord) => ({
                    lat: coord.latitude,
                    lng: coord.longitude,
                  }))}
                  options={zonePolygonOptions}
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    const mapState = preserveMapState();
                    setSelectedZone(zone);
                    restoreMapState(mapState);
                  }}
                />
              ) : null,
            )
          : null,
      )}
      {selectedZone && (
        <InfoWindow
          position={getPolygonCenter(selectedZone.zonePolygonJson)}
          onCloseClick={() => {
            const mapState = preserveMapState();
            setSelectedZone(null);
            restoreMapState(mapState);
          }}
        >
          <div>
            <p>
              <b>{t('zoneName')}:</b> {selectedZone.name || '--'}
            </p>
            <p>
              <b>{t('zoneId')}:</b> {selectedZone.code || '--'}
            </p>
          </div>
        </InfoWindow>
      )}
      {selectedLocationOrders.length > 0 && (
        <InfoWindow
          position={{
            lat: selectedLocationOrders[0].latitude,
            lng: selectedLocationOrders[0].longitude,
          }}
          onCloseClick={() => {
            setSelectedLocationOrders([]);
            setCurrentOrderIndex(0);
          }}
        >
          <div className="pl-5">
            {selectedLocationOrders.length > 1 && (
              <Row justify="space-between" align="middle" className="mb-5 pb-5">
                <Col>
                  <Button
                    onClick={() =>
                      setCurrentOrderIndex((prev) =>
                        prev > 0 ? prev - 1 : selectedLocationOrders.length - 1,
                      )
                    }
                  >
                    {'<'}
                  </Button>
                </Col>
                <Col className="ml-5 mr-5 mt-5 pl-5 pr-5 pt-5">
                  <p>
                    {t('order')}: {currentOrderIndex + 1} /{' '}
                    {selectedLocationOrders.length}
                  </p>
                </Col>
                <Col>
                  <Button
                    onClick={() =>
                      setCurrentOrderIndex((prev) =>
                        prev < selectedLocationOrders.length - 1 ? prev + 1 : 0,
                      )
                    }
                  >
                    {'>'}
                  </Button>
                </Col>
              </Row>
            )}
            <div>
              <p>
                <b>{t('orderId')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex]
                  .customerOrderNumber || '--'}
              </p>
              <p>
                <b>{t('orderStatus')}:</b>{' '}
                {t(selectedLocationOrders[currentOrderIndex].orderStatus) ||
                  '--'}
              </p>
              <p>
                <b>{t('ON_TIME_DELIVERY')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].onTime === true
                  ? t('yes')
                  : selectedLocationOrders[currentOrderIndex].onTime === false
                    ? t('no')
                    : '--'}
              </p>
              <p>
                <b>{t('directorate')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].directorate || '--'}
              </p>
              <p>
                <b>{t('region')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].region || '--'}
              </p>
              <p>
                <b>{t('city')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].city || '--'}
              </p>
              <p className="pt-5">
                <b>{t('storeWhereDelivered')}</b>
              </p>
              <p>
                <b>{t('storeName')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].store.name || '--'}
              </p>
              <p>
                <b>{t('storeCode')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].store.code || '--'}
              </p>
              <p>
                <b>{t('storeType')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].store.type || '--'}
              </p>
              <p className="pt-5">
                <b>{t('courier')}</b>
              </p>
              <p>
                <b>{t('courierName')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].courierName || '--'}
              </p>
              <p>
                <b>{t('courierIdentityNumber')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex]
                  .courierIdentityNumber || '--'}
              </p>
              <p>
                <b>{t('courierPhoneNumber')}:</b>{' '}
                {selectedLocationOrders[currentOrderIndex].courierPhoneNumber ||
                  '--'}
              </p>
            </div>
          </div>
        </InfoWindow>
      )}
      {polygonPath.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            right: 70,
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <span
            style={{
              backgroundColor: 'white',
              padding: '5px 10px',
              borderRadius: '2px',
            }}
          >
            {t('polygonArea')}: {calculatePolygonArea()} km²
          </span>
          <Button
            onClick={() => {
              clearCoordinates();
              setSelectedZone(null);
              setSelectedMarker(null);
            }}
          >
            {t('clear')}
          </Button>
        </div>
      )}
      {(mapLoading || loading) && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {heatmapData.length > 0 && (
        <Collapse
          defaultActiveKey={[]}
          expandIconPosition="end"
          style={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            width: 300,
          }}
        >
          <Collapse.Panel
            style={{ fontWeight: '500' }}
            header={t('heatmapSettings')}
            key="1"
          >
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <div>{t('Radius')}</div>
                <Row gutter={12}>
                  <Col span={16}>
                    <Slider
                      min={10}
                      max={100}
                      value={heatmapRadius}
                      onChange={setHeatmapRadius}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      min={10}
                      max={100}
                      value={heatmapRadius}
                      onChange={setHeatmapRadius}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <div>{t('Opacity')}</div>
                <Row gutter={12}>
                  <Col span={16}>
                    <Slider
                      min={0}
                      max={1}
                      step={0.1}
                      value={heatmapOpacity}
                      onChange={setHeatmapOpacity}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      min={0}
                      max={1}
                      step={0.1}
                      value={heatmapOpacity}
                      onChange={setHeatmapOpacity}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <div>{t('Max Intensity')}</div>
                <Row gutter={12}>
                  <Col span={16}>
                    <Slider
                      min={1}
                      max={orderDetails?.length || 100}
                      value={heatmapMaxIntensity}
                      onChange={setHeatmapMaxIntensity}
                    />
                  </Col>
                  <Col span={8}>
                    <InputNumber
                      min={1}
                      max={orderDetails?.length || 100}
                      value={heatmapMaxIntensity}
                      onChange={setHeatmapMaxIntensity}
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={24} className="pt-5">
                <Checkbox
                  checked={showClusteredMarkers}
                  onChange={(e) => setShowClusteredMarkers(e.target.checked)}
                >
                  {t('showClusteredMarkers')}
                </Checkbox>
              </Col>
              <Col span={24} className="pt-5">
                <Checkbox
                  checked={includeDeliveryFromStore}
                  onChange={(e) => {
                    setIncludeDeliveryFromStore(e.target.checked);
                    onIncludeDeliveryFromStoreChange(e.target.checked);
                  }}
                >
                  {t('includeDeliveryFromStore')}
                </Checkbox>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>
      )}
    </GoogleMap>
  );
};

export default OrderAnalysisMap;
